"use client";

import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { useDragScroll } from "@/hooks/use-drag-scroll";

/**
 * Figma 1468:4152 + 1468:4154 — 리뷰 섹션 타이틀 + 가로 스크롤 후기 캐러셀.
 * 카드(1484:964): 400x260, rounded-32, 옐로 별 5개 + 후기 20px + 작성자 16px.
 * Figma 원본은 동일 카드 7장 반복 placeholder → 실제 후기 8종(i18n) 렌더링.
 * 하단 우측 화살표(1468:4273)로 스크롤.
 */
const ITEMS = ["1", "2", "3", "4", "5", "6", "7", "8"] as const;

export function GolffixReviewsSection() {
  const t = useTranslations("www");
  // PC 마우스 드래그 + 관성(플링) 스크롤 — /apex 갤러리와 동일 공용 훅.
  const { ref: scrollRef, isDragging, handlers } = useDragScroll();
  // 스크롤 양 끝 여부 — 화살표 활성/비활성 색상 결정(/apex 갤러리와 동일 패턴).
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = 0; // 최초 위치를 처음(첫 카드)으로 고정
    const update = () => {
      setCanPrev(el.scrollLeft > 1);
      setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [scrollRef]);

  const scroll = (direction: number) => {
    // 카드 폭(≈clamp 최대 360) + gap-9(36) 기준 1장 이동
    scrollRef.current?.scrollBy({ left: direction * 396, behavior: "smooth" });
  };

  return (
    // 섹션 패딩·헤딩→콘텐츠 간격은 feature-modes 와 동일한 clamp 스케일 —
    // 고정 py-[3.75rem] 두 겹이 모바일에서 헤딩·트랙 사이를 120px 로 벌리던 것을 정리.
    <section className="w-full overflow-hidden py-[clamp(52px,7vw,80px)]">
      <div className="mx-auto max-w-7xl px-8 text-center">
        <div className="flex flex-col gap-display-gap leading-[1.3]">
          <h2 className="text-display-2 text-gray-800">
            {t("global_testimonial")}
          </h2>
        </div>
      </div>

      <div className="mt-[clamp(40px,6vw,80px)] flex flex-col gap-9">
        {/* 트랙은 뷰포트 전체 폭을 사용한다(피드백: width 100%) — 넓은 화면에서도
            카드가 컨테이너에 갇히지 않고 화면 끝까지 이어져 스크롤된다. */}
        <div
          ref={scrollRef}
          {...handlers}
          className={`scrollbar-none flex w-full gap-9 overflow-x-auto px-8 pb-4 ${
            isDragging ? "cursor-grabbing" : "cursor-grab"
          }`}
        >
          {ITEMS.map(key => (
            <article
              key={key}
              className="flex min-h-[16.25rem] w-[clamp(280px,32vw,360px)] shrink-0 flex-col justify-between gap-6 rounded-2xl border border-gray-100 bg-white p-6 shadow-[0px_11px_15px_rgba(0,0,0,0.15)] select-none"
            >
              <div className="flex flex-col gap-6">
                <div className="flex gap-0.5" aria-label="5 / 5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      aria-hidden
                      fill="#ffa100"
                      className="size-5 fill-[#ffa100] text-[#ffa100]"
                    />
                  ))}
                </div>
                <p className="text-body-3 text-gray-500">
                  {t(`user_review_${key}`)}
                </p>
              </div>
              <p className="text-caption text-gray-400">
                {t(`user_review_${key}_author`)}
              </p>
            </article>
          ))}
        </div>

        {/* /apex 갤러리와 동일 스타일 — 우측 정렬 맨 chevron + 이동 가능/불가 색상.
            라이트 배경이라 색은 반전: 가능=진한 회색(gray-600), 불가=연한 회색(gray-300). */}
        {/* 트랙과 오른쪽 끝을 맞추기 위해 화살표 행도 전체 폭 사용. */}
        <div className="flex w-full justify-end gap-4 px-8">
          <button
            type="button"
            onClick={() => scroll(-1)}
            disabled={!canPrev}
            aria-label="Previous reviews"
            className={`flex size-12 items-center justify-center transition-colors ${
              canPrev
                ? "text-gray-600 hover:text-gray-900"
                : "cursor-default text-gray-300"
            }`}
          >
            <ChevronLeft className="size-6" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            disabled={!canNext}
            aria-label="Next reviews"
            className={`flex size-12 items-center justify-center transition-colors ${
              canNext
                ? "text-gray-600 hover:text-gray-900"
                : "cursor-default text-gray-300"
            }`}
          >
            <ChevronRight className="size-6" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
}
